import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import { openPlusHistoryUpgrade } from '@/lib/openPlusHistoryUpgrade';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SidebarListItemLayout from './SidebarListItemLayout';
import { PLUS_COLORS, isDarkTheme } from '@/constants/Colors';

// Locked-history block at the bottom of the sidebar chat list for churned-
// Plus users (PLUS_WINBACK_PRESSURE.md §1). Mirrors the in-thread message
// lock: a CTA up top, then a couple of REAL hidden chats rendered blurred
// (so the history feels present, not deleted), then a slim "still in your
// history" reminder at the very bottom.
//
//   [ Upgrade card (CTA) ]      ← gradient hero gate
//   [ blurred chat row ]        ← real hidden chats, blurred + inert
//   [ blurred chat row ]
//   [ slim reminder bar ]       ← "N chats · still in your history"
//
// Canonical Plus brand gradient (derived from PLUS_COLORS — NEVER the theme
// accent).
const PLUS_GRADIENT = ['#0D5C47', '#0D5C47'];

const createStyles = (colors) => StyleSheet.create({
  pressed: {
    opacity: 0.92,
  },

  // ── Gradient hero upgrade card ────────────────────────────────────────
  cardContainer: {
    marginTop: 10,
    marginHorizontal: 6,
    marginBottom: 8,
    borderRadius: 12,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 8px 22px rgba(110, 58, 220, 0.4)',
    }),
  },
  cardGradient: {
    padding: 16,
    gap: 12,
  },
  cardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
  },
  cardBadge: {
    width: 38,
    height: 38,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.32)',
    flexShrink: 0,
  },
  cardTopText: {
    flex: 1,
    minWidth: 0,
  },
  cardHeadline: {
    color: '#fff',
    ...(Platform.OS === 'web' && { textShadow: '0 1px 2px rgba(0,0,0,0.2)' }),
  },
  cardSubtext: {
    color: 'rgba(255, 255, 255, 0.85)',
    lineHeight: 16,
  },
  cardCtaPill: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    backgroundColor: '#fff',
    paddingVertical: 10,
    borderRadius: 999,
  },
  cardCtaText: {
    color: '#5b2bd6',
  },

  // ── Blurred preview of hidden chats ───────────────────────────────────
  previewWrap: {
    marginHorizontal: 6,
    marginBottom: 6,
    position: 'relative',
  },
  previewStack: {
    ...(Platform.OS === 'web' && {
      filter: 'blur(4px)',
      userSelect: 'none',
    }),
  },
  previewBlur: {
    ...StyleSheet.absoluteFillObject,
  },
  // On web this deepens the frosted feel. On native it tints the BlurView
  // output AND acts as a safety net: Android only truly blurs when the
  // experimental method is active, so the scrim must obscure the titles on its
  // own if the blur degrades to a tint-only fallback.
  previewScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.solid_color_block,
    opacity: Platform.OS === 'web' ? 0.05 : 0.5,
  },

  // ── Slim footer reminder ──────────────────────────────────────────────
  slimBar: {
    marginTop: 2,
    marginHorizontal: 6,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderRadius: 10,
    backgroundColor: colors.solid_color_tertiary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  slimDot: {
    width: 26,
    height: 26,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  slimTextBlock: {
    flex: 1,
    minWidth: 0,
    gap: 1,
  },
  slimLabel: {
    color: colors.text_color_secondary,
    lineHeight: 16,
  },
  slimCount: {
    color: colors.text_color_primary,
  },
  slimCtaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  slimCtaText: {
    color: PLUS_COLORS.textColorPurple,
  },
});

function UpgradeCard({ hiddenCount, styles, onPress }) {
  return (
    <View style={styles.cardContainer}>
      <LinearGradient
        colors={PLUS_GRADIENT}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.cardGradient}
      >
        <View style={styles.cardTopRow}>
          <View style={styles.cardBadge}>
            <Icon name="lock" size={16} color={PLUS_COLORS.iconColor} solid />
          </View>
          <View style={styles.cardTopText}>
            <Text size="n" weight="bold" style={styles.cardHeadline}>
              {hiddenCount.toLocaleString()} {hiddenCount === 1 ? 'chat' : 'chats'} hidden
            </Text>
            <Text size="xs" style={styles.cardSubtext}>
              Restore Plus to see your full history.
            </Text>
          </View>
        </View>

        <Pressable
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel="Restore Plus"
          style={({ pressed }) => [styles.cardCtaPill, pressed && styles.pressed]}
        >
          <Icon name="sparkles" size={14} color="#5b2bd6" solid />
          <Text size="n" weight="bold" style={styles.cardCtaText}>Restore Plus</Text>
        </Pressable>
      </LinearGradient>
    </View>
  );
}

// A couple of REAL hidden chats rendered with the same row layout the
// sidebar uses, then blurred + made inert. Tapping anywhere routes to the
// upgrade modal (the real rows never navigate).
function LockedPreviewRows({ preview, styles, onPress, blurTint }) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Restore Plus to see these conversations"
      style={styles.previewWrap}
    >
      <View style={styles.previewStack} pointerEvents="none">
        {preview.map((chat) => (
          <SidebarListItemLayout
            key={chat.id}
            title={chat.title || 'Untitled chat'}
            noEllipsis
          />
        ))}
      </View>
      {/* Native has no CSS blur — overlay a real BlurView so the hidden chat
          titles are genuinely unreadable (web uses the stack filter). */}
      {Platform.OS !== 'web' && (
        <BlurView
          intensity={40}
          tint={blurTint}
          experimentalBlurMethod="dimezisBlurView"
          style={styles.previewBlur}
          pointerEvents="none"
        />
      )}
      <View style={styles.previewScrim} pointerEvents="none" />
    </Pressable>
  );
}

function SlimReminderBar({ hiddenCount, styles, onPress }) {
  const countLabel = hiddenCount.toLocaleString();
  const chatWord = hiddenCount === 1 ? 'chat' : 'chats';
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Welcome back to Plus to reopen ${countLabel} ${chatWord} still in your history`}
      style={({ pressed }) => [styles.slimBar, pressed && styles.pressed]}
    >
      <LinearGradient
        colors={PLUS_GRADIENT}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.slimDot}
      >
        <Icon name="lock" size={12} color={PLUS_COLORS.iconColor} solid />
      </LinearGradient>
      <View style={styles.slimTextBlock}>
        <Text size="xs" weight="medium" numberOfLines={2} style={styles.slimLabel}>
          <Text size="xs" weight="bold" style={styles.slimCount}>{countLabel} {chatWord}</Text>
          {' still in your history'}
        </Text>
        <View style={styles.slimCtaRow}>
          <Text size="xs" weight="bold" style={styles.slimCtaText}>Welcome back</Text>
          <Icon name="arrow-right" size={10} color={PLUS_COLORS.textColorPurple} solid />
        </View>
      </View>
    </Pressable>
  );
}

function SidebarPlusLockedCard({ hiddenCount, lockedPreview }) {
  const { colors, theme } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const blurTint = isDarkTheme(theme) ? 'dark' : 'light';
  const { openModal } = useModalStore(state => state.actions);

  const handlePress = useCallback(() => {
    openPlusHistoryUpgrade(openModal, 'plus-lock-sidebar');
  }, [openModal]);

  if (!hiddenCount || hiddenCount <= 0) return null;

  const preview = Array.isArray(lockedPreview) ? lockedPreview : [];
  const hasPreview = preview.length > 0;

  return (
    <View>
      <UpgradeCard hiddenCount={hiddenCount} styles={styles} onPress={handlePress} />
      {hasPreview ? (
        <>
          <LockedPreviewRows preview={preview} styles={styles} onPress={handlePress} blurTint={blurTint} />
          <SlimReminderBar hiddenCount={hiddenCount} styles={styles} onPress={handlePress} />
        </>
      ) : null}
    </View>
  );
}

SidebarPlusLockedCard.propTypes = {
  hiddenCount: PropTypes.number.isRequired,
  lockedPreview: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
};

export default React.memo(SidebarPlusLockedCard);
